import React from 'react';
import './TermsOfUse.css';
import { Link, useLocation } from 'react-router-dom';

function TermsOfUse() {
  const effectiveDate = 'February 20, 2026';
  const appName = 'GlowAI';
  const location = useLocation();

  return (
    <div className="terms-of-use">
      <header className="terms-header">
        <div className="header-content">
          <h1>Terms of Use</h1>
          <p className="subtitle">Please read these terms carefully</p>
        </div>
      </header>

      <main className="terms-content">
        <p className="effective-date">Effective Date: {effectiveDate}</p>

        <section>
          <h2>1. Acceptance of Terms</h2>
          <p>
            By downloading, installing, or using the {appName} mobile application
            ("App"), you agree to be bound by these Terms of Use ("Terms"). If you
            do not agree to these Terms, please do not use the App.
          </p>
        </section>

        <section>
          <h2>2. Description of Service</h2>
          <p>
            {appName} is an AI-powered skin care application that helps you analyze
            your skin health, track your skin care routines, and receive personalized
            skin care recommendations. The App is available on iOS through the
            Apple App Store.
          </p>
        </section>

        <section>
          <h2>3. Eligibility</h2>
          <p>
            You must be at least 13 years of age to use this App. By using the App,
            you represent and warrant that you meet this age requirement. If you are
            under 18, you should review these Terms with a parent or guardian to
            ensure they understand and agree to them.
          </p>
        </section>

        <section>
          <h2>4. Free and Premium Features</h2>
          <p>
            The App offers both free and premium features. Certain features may
            require an in-app purchase or subscription ("Premium Features"). By
            purchasing Premium Features, you agree to the pricing and payment terms
            presented at the time of purchase.
          </p>

          <h3>4.1 In-App Purchases</h3>
          <ul>
            <li>All purchases are processed through the Apple App Store</li>
            <li>Prices are displayed before you confirm any purchase</li>
            <li>
              Subscriptions automatically renew unless cancelled at least 24 hours
              before the end of the current billing period
            </li>
            <li>
              You can manage or cancel your subscriptions through your Apple ID
              account settings
            </li>
          </ul>

          <h3>4.2 Refunds</h3>
          <p>
            Refund requests are handled by Apple in accordance with their refund
            policies. We do not process refunds directly.
          </p>
        </section>

        <section>
          <h2>5. User Responsibilities</h2>
          <p>When using the App, you agree to:</p>
          <ul>
            <li>Use the App only for its intended purpose of skin care management</li>
            <li>Not attempt to reverse engineer, decompile, or disassemble the App</li>
            <li>
              Not use the App for any unlawful, harmful, or fraudulent activities
            </li>
            <li>
              Not interfere with or disrupt the App's functionality or servers
            </li>
            <li>
              Not attempt to gain unauthorized access to any part of the App
            </li>
          </ul>
        </section>

        <section>
          <h2>6. Intellectual Property</h2>
          <p>
            All content, features, and functionality of the App — including but not
            limited to text, graphics, logos, icons, images, AI models, and software —
            are the exclusive property of {appName} and are protected by copyright,
            trademark, and other intellectual property laws. You may not reproduce,
            distribute, modify, or create derivative works from any part of the App
            without our prior written consent.
          </p>
        </section>

        <section>
          <h2>7. Skin Care Disclaimer</h2>
          <p>
            The skin care information, AI analysis results, and recommendations provided
            by the App are for general informational purposes only and do not constitute
            medical advice. We do not guarantee the accuracy, completeness, or
            suitability of any skin care advice. Results may vary depending on your
            individual skin type, conditions, and other factors. Always consult a
            qualified dermatologist or healthcare professional before making significant
            changes to your skin care regimen. We are not responsible for any adverse
            reactions or outcomes resulting from the use of the App's recommendations.
          </p>
        </section>

        <section>
          <h2>8. Data and Privacy</h2>
          <p>
            Your use of the App is also governed by our{' '}
            <Link
              to="/"
              className={`terms-link ${location.pathname === '/' ? 'active' : ''}`}
            >
              Privacy Policy
            </Link>
            , which describes how we collect, use, and protect your information.
            Your skin care data and preferences are stored locally on your device.
          </p>
        </section>

        <section>
          <h2>9. Third-Party Services</h2>
          <p>
            The App may integrate with third-party services such as AI analysis
            providers and Firebase. Your use of these services is subject to their
            respective terms and conditions. We are not responsible for the content,
            privacy practices, or availability of third-party services.
          </p>
        </section>

        <section>
          <h2>10. Notifications</h2>
          <p>
            The App uses local notifications to send skin care routine reminders. By
            enabling notifications, you consent to receiving these reminders. You
            can disable notifications at any time through your device settings or
            within the App.
          </p>
        </section>

        <section>
          <h2>11. Disclaimer of Warranties</h2>
          <p>
            The App is provided on an "as is" and "as available" basis without
            warranties of any kind, either express or implied, including but not
            limited to implied warranties of merchantability, fitness for a
            particular purpose, and non-infringement. We do not warrant that the
            App will be uninterrupted, error-free, or free of harmful components.
          </p>
        </section>

        <section>
          <h2>12. Limitation of Liability</h2>
          <p>
            To the fullest extent permitted by applicable law, {appName} and its
            developers shall not be liable for any indirect, incidental, special,
            consequential, or punitive damages, including but not limited to loss
            of data, loss of profits, or damages arising from your use of or
            inability to use the App.
          </p>
        </section>

        <section>
          <h2>13. Termination</h2>
          <p>
            We reserve the right to terminate or suspend your access to the App at
            any time, without prior notice, for conduct that we believe violates
            these Terms or is harmful to other users, us, or third parties. Upon
            termination, your right to use the App will immediately cease.
          </p>
        </section>

        <section>
          <h2>14. Changes to These Terms</h2>
          <p>
            We may update these Terms from time to time. We will notify you of any
            changes by posting the updated Terms within the App and updating the
            effective date above. Your continued use of the App after any changes
            constitutes your acceptance of the revised Terms.
          </p>
        </section>

        <section>
          <h2>15. Governing Law</h2>
          <p>
            These Terms shall be governed by and construed in accordance with the
            laws of the jurisdiction in which the App developer resides, without
            regard to its conflict of law provisions.
          </p>
        </section>

        <section>
          <h2>16. Contact Us</h2>
          <p>
            If you have any questions or concerns about these Terms of Use, please
            contact us at:
          </p>
          <p className="contact-email">
            <Link
              to="/contact"
              className={`terms-link ${location.pathname === '/contact' ? 'active' : ''}`}
            >
              Contact Us
            </Link>
          </p>
        </section>
      </main>

      <footer className="terms-footer">
        <p>&copy; {new Date().getFullYear()} {appName}. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default TermsOfUse;
